import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button, Paper, List, ListItem, CircularProgress, Alert, Chip, Tooltip, Drawer, IconButton, Divider } from '@mui/material';
import { Send, Description, Lightbulb, Menu, ChevronRight, ChevronLeft, CheckCircle, RadioButtonUnchecked, RestartAlt } from '@mui/icons-material';
import { getAIFeedbackSync } from '../../Utils/aiUtils';
import { HLD_EVALUATION_SCHEMA } from '../../Utils/aiSchemas';
import { HLD_EVALUATION_PROMPT, DEEP_DIVE_HINT_PROMPT } from '../../Utils/aiPrompts';
import { postRequest } from '../../Fetch/request';
import SpeechToTextInput from '../Common/SpeechToTextInput';
import './HLD.css';

export default function DeepDive({ 
  problemId,
  problemTitle = '',
  deepDiveQuestions = [],
  onProgress = (currentQuestion, totalQuestions) => {},
  documentVisible = false,
  setDocumentVisible = () => {}
}) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [followUpQuestions, setFollowUpQuestions] = useState([]); // Array to track all follow-up questions for current question
  const [userAnswer, setUserAnswer] = useState('');
  const [followUpAnswer, setFollowUpAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [questionNavOpen, setQuestionNavOpen] = useState(false);
  const chatEndRef = useRef(null);
  const hasInitializedQuestionIndex = useRef(false);
  // Refs to store latest values for cleanup on unmount
  const userAnswerRef = useRef('');
  const followUpAnswerRef = useRef('');
  const currentQuestionIndexRef = useRef(0);
  // Track whether user has actually typed (prevents initial mount from clobbering saved draft)
  const hasUserTypedRef = useRef(false);

  const currentQuestion = deepDiveQuestions[currentQuestionIndex];
  
  // Keep refs in sync with state
  useEffect(() => {
    userAnswerRef.current = userAnswer;
  }, [userAnswer]);
  
  useEffect(() => {
    followUpAnswerRef.current = followUpAnswer;
  }, [followUpAnswer]);
  
  useEffect(() => {
    currentQuestionIndexRef.current = currentQuestionIndex;
  }, [currentQuestionIndex]);
  
  // Calculate which follow-up we're currently on based on conversation
  // Simplified logic: support infinite follow-ups - just find the last unanswered follow-up
  const { currentFollowUpIndex, isFollowUpPhase, currentFollowUpQuestion } = useMemo(() => {
    // Check chat messages to find the last unanswered follow-up question
    // Look through messages in reverse to find the most recent follow-up question without an answer
    let lastUnansweredFollowUp = null;
    let lastUnansweredIndex = -1;
    
    // Check chat messages for follow-up questions
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const msg = chatMessages[i];
      if (msg.isFollowUp && msg.isQuestion) {
        // Found a follow-up question - check if there's an answer after it
        const messagesAfter = chatMessages.slice(i + 1);
        const hasAnswerAfter = messagesAfter.some(m => m.type === 'user');
        
        if (!hasAnswerAfter) {
          // This follow-up question doesn't have an answer yet
          lastUnansweredFollowUp = msg.content;
          // Find its index in followUpQuestions array
          lastUnansweredIndex = followUpQuestions.findIndex(q => q === msg.content);
          break;
        }
      }
    }
    
    // If no unanswered follow-up found in chat, check if there are follow-ups in the array
    // that haven't been added to chat yet (shouldn't happen, but handle it)
    if (lastUnansweredFollowUp === null && followUpQuestions.length > 0) {
      // Check if the last follow-up in the array has been answered
      const lastFollowUp = followUpQuestions[followUpQuestions.length - 1];
      const lastFollowUpInChat = chatMessages.find(m => 
        m.isFollowUp && m.isQuestion && m.content === lastFollowUp
      );
      
      if (lastFollowUpInChat) {
        // Check if it has an answer
        const lastFollowUpIndex = chatMessages.findIndex(m => m.id === lastFollowUpInChat.id);
        const messagesAfter = chatMessages.slice(lastFollowUpIndex + 1);
        const hasAnswer = messagesAfter.some(m => m.type === 'user');
        
        if (!hasAnswer) {
          lastUnansweredFollowUp = lastFollowUp;
          lastUnansweredIndex = followUpQuestions.length - 1;
        }
      } else {
        // Last follow-up not in chat yet - shouldn't happen, but use it
        lastUnansweredFollowUp = lastFollowUp;
        lastUnansweredIndex = followUpQuestions.length - 1;
      }
    }
    
    const isPhase = lastUnansweredFollowUp !== null;
    const question = lastUnansweredFollowUp || '';
    
    return { 
      currentFollowUpIndex: lastUnansweredIndex, 
      isFollowUpPhase: isPhase, 
      currentFollowUpQuestion: question 
    };
  }, [followUpQuestions, chatMessages]);
  const progressPercent = currentQuestion ? ((currentQuestionIndex + 1) / deepDiveQuestions.length) * 100 : 100;
  const [lastLoadedTimestamp, setLastLoadedTimestamp] = useState(null);

  // Message types for better tracking
  const MESSAGE_TYPES = {
    QUESTION: 'question',
    USER_ANSWER: 'user_answer',
    AI_FEEDBACK: 'ai_feedback',
    FOLLOWUP_QUESTION: 'followup_question',
    HINT_ASKED: 'hint_asked',
    AI_HINT: 'ai_hint'
  };

  // Function to load progress from localStorage
  // IMPORTANT: Only set initial question index on first load, don't change it automatically
  const loadProgressFromLocalStorage = React.useCallback(() => {
    if (problemId && deepDiveQuestions.length > 0) {
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      
      // Check if timestamp is newer than what we last loaded
      const currentTimestamp = attemptData.timestamp || 0;
      if (lastLoadedTimestamp !== null && currentTimestamp <= lastLoadedTimestamp) {
        // No update needed
        return;
      }
      
      // Update last loaded timestamp
      setLastLoadedTimestamp(currentTimestamp);
      
      const conversations = attemptData.deepDiveConversations || attemptData.steps?.deepDives?.conversations || [];
      
      // Derive completed questions from conversations (for display purposes only)
      const completed = [];
      conversations.forEach((convObj, index) => {
        const messages = convObj?.conversation || (Array.isArray(convObj) ? convObj : []);
        
        if (!messages || !Array.isArray(messages)) {
          return;
        }
        
        // Mark as completed if it has at least main question + answer + feedback
          const hasMainQuestion = messages.some(msg => msg.isQuestion && !msg.isFollowUp);
        const hasMainAnswer = messages.some(msg => msg.type === 'user');
        const hasMainFeedback = messages.some(msg => msg.isFeedback);
          
        if (hasMainQuestion && hasMainAnswer && hasMainFeedback) {
            completed.push(index);
        }
      });
      
      setCompletedQuestions(completed);
      
      // IMPORTANT: Only set currentQuestionIndex on initial load (first time only)
      // Don't automatically change it - user should stay on the question they're working on
      // NEVER automatically move to next question - user must manually navigate
      if (!hasInitializedQuestionIndex.current) {
        hasInitializedQuestionIndex.current = true;
        
        // Try to restore last question index from localStorage
        const lastQuestionIndex = attemptData.lastDeepDiveQuestionIndex;
        let questionIndexToSet = 0;
        
        if (lastQuestionIndex !== undefined && lastQuestionIndex >= 0 && lastQuestionIndex < deepDiveQuestions.length) {
          // Restore last question user was on (user's choice, not automatic)
          questionIndexToSet = lastQuestionIndex;
        } else {
          // On first load, start at question 0 (don't skip to unanswered questions)
          // User can manually navigate to any question they want
          questionIndexToSet = 0;
        }
        
        setCurrentQuestionIndex(questionIndexToSet);
        onProgress(questionIndexToSet, deepDiveQuestions.length);
      } else {
        // Just update progress for current question (don't change question index)
        // NEVER automatically change question index after initial load
        onProgress(currentQuestionIndex, deepDiveQuestions.length);
      }
      
      // Clean up the old progress key if it exists
      const oldProgressKey = `hld-deepdive-progress-${problemId}`;
      localStorage.removeItem(oldProgressKey);
    }
  }, [problemId, deepDiveQuestions.length, lastLoadedTimestamp, currentQuestionIndex]);

  // Function to load conversations from localStorage
  const loadConversationsFromLocalStorage = React.useCallback(() => {
    if (!problemId || deepDiveQuestions.length === 0) {
      return;
    }
    
    // Ensure currentQuestionIndex is valid
    if (currentQuestionIndex < 0 || currentQuestionIndex >= deepDiveQuestions.length) {
      return;
    }
    
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Update timestamp tracking if backend data was loaded
    const currentTimestamp = attemptData.timestamp || 0;
    if (lastLoadedTimestamp === null || currentTimestamp > lastLoadedTimestamp) {
      setLastLoadedTimestamp(currentTimestamp);
    }
    
      const allConversations = attemptData.deepDiveConversations || [];
      
    // Build conversation history for CURRENT QUESTION ONLY
    // Each question has its own separate chat area
      let allMessages = [];
      const seenMessageIds = new Set(); // Track message IDs to prevent duplicates
      
    // Only load messages for the current question (not previous questions)
    const question = deepDiveQuestions[currentQuestionIndex];
    
    if (!question) {
      return;
    }
    
    const savedConversation = allConversations.find(c => c.questionIndex === currentQuestionIndex);
        
        if (savedConversation && savedConversation.conversation && savedConversation.conversation.length > 0) {
      // Get saved messages for this question only
          const savedMessages = savedConversation.conversation;
          
      // Add ALL messages from this question's conversation (support infinite follow-ups)
          // Also add messageType if it doesn't exist (for backward compatibility)
      savedMessages.forEach(msg => {
            if (!seenMessageIds.has(msg.id)) {
              seenMessageIds.add(msg.id);
              // Add messageType if it doesn't exist (backward compatibility)
              if (!msg.messageType) {
                msg.messageType = 
                  msg.isQuestion && !msg.isFollowUp ? MESSAGE_TYPES.QUESTION :
                  msg.isFollowUp && msg.isQuestion ? MESSAGE_TYPES.FOLLOWUP_QUESTION :
                  msg.isFeedback ? MESSAGE_TYPES.AI_FEEDBACK :
              msg.isHint ? MESSAGE_TYPES.AI_HINT :
              msg.type === 'user' && msg.content === 'can you give me a hint?' ? MESSAGE_TYPES.HINT_ASKED :
                  msg.type === 'user' ? MESSAGE_TYPES.USER_ANSWER :
                  MESSAGE_TYPES.AI_FEEDBACK; // Default fallback
              }
          // Preserve hint flags if they exist
          if (msg.isHint !== undefined) {
            msg.isHint = msg.isHint;
          }
          if (msg.isHintRequest !== undefined) {
            msg.isHintRequest = msg.isHintRequest;
              }
              allMessages.push(msg);
            }
          });
    } else {
          // Current question with no saved conversation - add the question itself
      const questionMessage = {
        id: `question-${currentQuestionIndex}-${Date.now()}`,
            messageType: MESSAGE_TYPES.QUESTION,
            type: 'ai', // Keep for backward compatibility
            content: question.question,
        timestamp: new Date().toISOString(),
        isQuestion: true
      };
      
          if (!seenMessageIds.has(questionMessage.id)) {
            seenMessageIds.add(questionMessage.id);
            allMessages.push(questionMessage);
        }
      }
      
      // Restore state from current question's conversation
      const currentQuestionConversation = allConversations.find(c => c.questionIndex === currentQuestionIndex);
      let stateRestoredFromConversation = false;
      
      if (currentQuestionConversation && currentQuestionConversation.conversation && currentQuestionConversation.conversation.length > 0) {
        stateRestoredFromConversation = true;
        const savedCurrentMessages = currentQuestionConversation.conversation;
        
        // Extract all follow-up questions from conversation (infinite follow-ups support)
        // Sort by timestamp to get them in order
        const followUpQuestionsInConversation = savedCurrentMessages
          .filter(m => m.isFollowUp && m.isQuestion)
          .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0))
          .map(m => m.content)
          .filter(Boolean);
        
        // Set all follow-up questions (support infinite follow-ups)
        setFollowUpQuestions(followUpQuestionsInConversation);
        
        // Set feedback state if there's feedback in the conversation
        const feedbackInConversation = savedCurrentMessages.find(m => m.isFeedback);
        if (feedbackInConversation) {
          if (feedbackInConversation.evaluation) {
            setFeedback(feedbackInConversation.evaluation);
          } else if (feedbackInConversation.verdict) {
            setFeedback({
              verdict: feedbackInConversation.verdict,
              feedback: feedbackInConversation.feedback || feedbackInConversation.content,
              followUpQuestion: feedbackInConversation.followUpQuestion || ''
            });
          }
        } else {
          setFeedback(null);
        }
      } else {
        // No saved conversation for current question - reset state
        setFollowUpQuestions([]);
        setFeedback(null);
      }
      
      // Sort messages by timestamp to ensure correct chronological order
      allMessages.sort((a, b) => {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        return timeA - timeB;
      });
      
      setChatMessages(allMessages);
      
      // Always restore userAnswer and followUpAnswer from localStorage FIRST
      // Use !== undefined to preserve empty strings that were intentionally saved
      if (problemId) {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndex}`;
        const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndex}`;
        
        // Restore userAnswer if it exists in localStorage (including empty strings)
        // Always restore if we have a saved value, even if it's an empty string
        if (attemptData[savedUserAnswerKey] !== undefined) {
          setUserAnswer(attemptData[savedUserAnswerKey]);
        }
        // If no saved value exists, don't clear - preserve any existing input
        // This prevents clearing user input when navigating between steps
        
        // Restore followUpAnswer if it exists in localStorage (including empty strings)
        // Always restore if we have a saved value, even if it's an empty string
        if (attemptData[savedFollowUpAnswerKey] !== undefined) {
          setFollowUpAnswer(attemptData[savedFollowUpAnswerKey]);
        }
        // If no saved value exists, don't clear - preserve any existing input
        // This prevents clearing user input when navigating between steps
      } else {
        // No problemId, clear answers
        setUserAnswer('');
        setFollowUpAnswer('');
      }
      
      // Only reset state if we didn't already restore it from saved conversation above
      if (!stateRestoredFromConversation) {
        // No state restored from saved conversation - reset state
        setFollowUpQuestions([]);
        setFeedback(null);
      }
  }, [currentQuestionIndex, problemId, deepDiveQuestions.length, lastLoadedTimestamp]);
  
  // Save userAnswer to localStorage whenever it changes
  // Guard: skip saving empty string unless user has actually typed (prevents mount from clobbering saved draft)
  useEffect(() => {
    if (problemId && userAnswer !== undefined && currentQuestionIndex !== undefined && currentQuestionIndex >= 0) {
      // Only save if user has typed something, OR if the value is non-empty
      // This prevents the initial '' state on mount from overwriting a previously saved draft
      if (hasUserTypedRef.current || userAnswer !== '') {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndex}`;
        attemptData[savedUserAnswerKey] = userAnswer;
        attemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      }
    }

    // Cleanup: Save on unmount when navigating away - use refs to get latest values
    return () => {
      if (problemId) {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const latestQuestionIndex = currentQuestionIndexRef.current;
        const latestUserAnswer = userAnswerRef.current;

        if (latestQuestionIndex !== undefined && latestQuestionIndex >= 0 && latestUserAnswer !== undefined) {
          const savedUserAnswerKey = `deepDiveUserAnswer_${latestQuestionIndex}`;
          attemptData[savedUserAnswerKey] = latestUserAnswer;
          attemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(attemptData));
        }
      }
    };
  }, [userAnswer, problemId, currentQuestionIndex]);
  
  // Save followUpAnswer to localStorage whenever it changes (but skip initial empty string)
  useEffect(() => {
    if (problemId && followUpAnswer !== undefined && currentQuestionIndex !== undefined && currentQuestionIndex >= 0) {
      if (hasUserTypedRef.current || followUpAnswer !== '') {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndex}`;
        attemptData[savedFollowUpAnswerKey] = followUpAnswer;
        attemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      }
    }

    // Cleanup: Save on unmount when navigating away - use refs to get latest values
    return () => {
      if (problemId) {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const latestQuestionIndex = currentQuestionIndexRef.current;
        const latestFollowUpAnswer = followUpAnswerRef.current;

        if (latestQuestionIndex !== undefined && latestQuestionIndex >= 0 && latestFollowUpAnswer !== undefined) {
          const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${latestQuestionIndex}`;
          attemptData[savedFollowUpAnswerKey] = latestFollowUpAnswer;
          attemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(attemptData));
        }
      }
    };
  }, [followUpAnswer, problemId, currentQuestionIndex]);
  
  // Explicitly restore answers when component mounts or when returning from another step
  // This ensures persistence even when navigating between steps
  // Run AFTER loadConversationsFromLocalStorage to ensure it doesn't get overwritten
  useEffect(() => {
    if (problemId && currentQuestionIndex !== undefined && currentQuestionIndex >= 0 && deepDiveQuestions.length > 0) {
      // Use a small delay to ensure loadConversationsFromLocalStorage has finished
      const restoreTimeout = setTimeout(() => {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndex}`;
        const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndex}`;
        
        // Always restore from localStorage if it exists (overwrite current state)
        // This ensures persistence when coming back from another step
        // Only restore if we have a saved value - preserve any existing input
        if (attemptData[savedUserAnswerKey] !== undefined) {
          setUserAnswer(attemptData[savedUserAnswerKey]);
        }
        
        if (attemptData[savedFollowUpAnswerKey] !== undefined) {
          setFollowUpAnswer(attemptData[savedFollowUpAnswerKey]);
        }
      }, 200); // Slightly longer delay to ensure loadConversationsFromLocalStorage completes and all data is loaded
      
      return () => clearTimeout(restoreTimeout);
    }
  }, [problemId, currentQuestionIndex, deepDiveQuestions.length]); // Run when component mounts or question index changes

  // Load progress from deep dive conversations in hld-attempt-${problemId}
  // Derive current question index and completed questions from stored conversations
  useEffect(() => {
    loadProgressFromLocalStorage();
  }, [loadProgressFromLocalStorage]);

  // Listen for storage events to detect when localStorage is updated (e.g., from backend load)
  useEffect(() => {
    if (!problemId) return;

    const attemptKey = `hld-attempt-${problemId}`;

    const handleStorageChange = (e) => {
      if (e.key === attemptKey || (!e.key && localStorage.getItem(attemptKey))) {
        // Storage was updated, reload progress and conversations
        setTimeout(() => {
          loadProgressFromLocalStorage();
          loadConversationsFromLocalStorage();
          // Also restore input values in case they were preserved
          const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndexRef.current}`;
          const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndexRef.current}`;
          if (attemptData[savedUserAnswerKey] !== undefined) {
            setUserAnswer(attemptData[savedUserAnswerKey]);
          }
          if (attemptData[savedFollowUpAnswerKey] !== undefined) {
            setFollowUpAnswer(attemptData[savedFollowUpAnswerKey]);
          }
        }, 100); // Small delay to ensure data is written
      }
    };

    // Listen for custom localStorageUpdated event (fires when parent updates localStorage)
    const handleLocalStorageUpdate = (event) => {
      if (event.detail?.key === attemptKey) {
        setTimeout(() => {
          loadProgressFromLocalStorage();
          loadConversationsFromLocalStorage();
          // Also restore input values in case they were preserved
          const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndexRef.current}`;
          const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndexRef.current}`;
          if (attemptData[savedUserAnswerKey] !== undefined) {
            setUserAnswer(attemptData[savedUserAnswerKey]);
          }
          if (attemptData[savedFollowUpAnswerKey] !== undefined) {
            setFollowUpAnswer(attemptData[savedFollowUpAnswerKey]);
          }
        }, 100);
      }
    };

    // Listen for storage events (fires when localStorage is updated from other tabs/components)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom localStorageUpdated event (fires when parent updates localStorage in same tab)
    window.addEventListener('localStorageUpdated', handleLocalStorageUpdate);

    // Also check for updates periodically (in case storage event doesn't fire for same-tab updates)
    const checkInterval = setInterval(() => {
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      const currentTimestamp = attemptData.timestamp || 0;
      
      if (lastLoadedTimestamp !== null && currentTimestamp > lastLoadedTimestamp) {
        loadProgressFromLocalStorage();
        // Also reload conversations when timestamp changes (backend data loaded)
        setTimeout(() => {
          loadConversationsFromLocalStorage();
          // Also restore input values in case they were preserved
          const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndexRef.current}`;
          const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndexRef.current}`;
          if (attemptData[savedUserAnswerKey] !== undefined) {
            setUserAnswer(attemptData[savedUserAnswerKey]);
          }
          if (attemptData[savedFollowUpAnswerKey] !== undefined) {
            setFollowUpAnswer(attemptData[savedFollowUpAnswerKey]);
          }
        }, 100);
      } else if (lastLoadedTimestamp === null && currentTimestamp > 0) {
        // First load after component mount - backend data might have loaded
        loadProgressFromLocalStorage();
        // Also reload conversations when initial data is detected
        setTimeout(() => {
          loadConversationsFromLocalStorage();
          // Also restore input values in case they were preserved
          const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndexRef.current}`;
          const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndexRef.current}`;
          if (attemptData[savedUserAnswerKey] !== undefined) {
            setUserAnswer(attemptData[savedUserAnswerKey]);
          }
          if (attemptData[savedFollowUpAnswerKey] !== undefined) {
            setFollowUpAnswer(attemptData[savedFollowUpAnswerKey]);
          }
        }, 100);
      }
    }, 1000); // Check every second for updates

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdated', handleLocalStorageUpdate);
      clearInterval(checkInterval);
    };
  }, [problemId, lastLoadedTimestamp, loadProgressFromLocalStorage, loadConversationsFromLocalStorage]);

  // Load all conversations grouped by question and build complete chat history
  // Store conversation per question, loop over questions to show results
  useEffect(() => {
    loadConversationsFromLocalStorage();
  }, [loadConversationsFromLocalStorage]);

  // Ensure first question is shown immediately when component loads
  useEffect(() => {
    if (problemId && deepDiveQuestions.length > 0 && currentQuestion && chatMessages.length === 0) {
      // Component just loaded, show the first question
      const firstQuestionMessage = {
        id: `question-${currentQuestionIndex}-${Date.now()}`,
        type: 'ai',
        content: currentQuestion.question,
        timestamp: new Date().toISOString(),
        isQuestion: true
      };
      setChatMessages([firstQuestionMessage]);
    }
  }, [problemId, deepDiveQuestions.length, currentQuestion, currentQuestionIndex]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, loading]);

  // Filter messages to only include those belonging to a specific question index
  const filterMessagesForQuestion = (messages, questionIndex) => {
    // Find the main question message for this question index
    const mainQuestionId = `question-${questionIndex}-`;
    const mainQuestionIndex = messages.findIndex(msg => 
      msg.id && msg.id.startsWith(mainQuestionId) && msg.isQuestion && !msg.isFollowUp
    );
    
    if (mainQuestionIndex === -1) {
      // No main question found for this index, return empty array
      return [];
    }
    
    // Find the next question message (if it exists) to determine where this question's conversation ends
    const nextQuestionIndex = messages.findIndex((msg, index) => 
      index > mainQuestionIndex && 
      msg.id && 
      msg.id.startsWith(`question-${questionIndex + 1}-`) && 
      msg.isQuestion && 
      !msg.isFollowUp
    );
    
    // Extract messages between main question and next question (or end of array)
    const endIndex = nextQuestionIndex !== -1 ? nextQuestionIndex : messages.length;
    return messages.slice(mainQuestionIndex, endIndex);
  };

  // Filter conversation to only include questions, user answers, AI feedback, and hints
  // Exclude expected answers and other problem data
  const filterConversationForStorage = (messages) => {
    return messages
      .filter(msg => {
        // Include: questions, user answers, AI feedback (evaluations), hints
        // Exclude: error messages, completion messages, etc.
        return (
          (msg.isQuestion && msg.content) || // Questions (main and follow-up)
          (msg.type === 'user' && msg.content) || // User answers (including hint requests)
          (msg.isFeedback && msg.content) || // AI feedback
          (msg.type === 'ai' && msg.isQuestion && msg.content) || // AI questions (follow-ups)
          (msg.isHint && msg.content) || // AI hint responses
          (msg.messageType === MESSAGE_TYPES.HINT_ASKED && msg.content) || // Hint requests
          (msg.messageType === MESSAGE_TYPES.AI_HINT && msg.content) // AI hints
        );
      })
      .map(msg => {
        // Save complete message data including all AI evaluation fields and hint flags
        const messageData = {
          id: msg.id,
          messageType: msg.messageType || // New specific message type
            (msg.isQuestion && !msg.isFollowUp ? MESSAGE_TYPES.QUESTION :
            msg.isFollowUp && msg.isQuestion ? MESSAGE_TYPES.FOLLOWUP_QUESTION :
            msg.isFeedback ? MESSAGE_TYPES.AI_FEEDBACK :
            msg.isHint ? MESSAGE_TYPES.AI_HINT :
            msg.type === 'user' && msg.content === 'can you give me a hint?' ? MESSAGE_TYPES.HINT_ASKED :
            msg.type === 'user' ? MESSAGE_TYPES.USER_ANSWER :
            MESSAGE_TYPES.AI_FEEDBACK), // Default fallback
          type: msg.type, // 'user' or 'ai' - Keep for backward compatibility
          content: msg.content, // The actual text
          timestamp: msg.timestamp,
          isQuestion: msg.isQuestion || false, // Whether it's a question
          isFollowUp: msg.isFollowUp || false, // Whether it's a follow-up question
          isFeedback: msg.isFeedback || false, // Whether it's AI feedback
          isHint: msg.isHint || false, // Whether it's an AI hint response
          isHintRequest: msg.isHintRequest || (msg.content === 'can you give me a hint?' && msg.type === 'user') || false, // Whether it's a hint request
        };
        
        // Add AI evaluation fields if present
        if (msg.verdict !== undefined && msg.verdict !== null) {
          messageData.verdict = msg.verdict; // PASS/PARTIAL/FAIL
        }
        
        // Store complete AI evaluation object if available (all 3 fields: verdict, feedback, followUpQuestion)
        if (msg.evaluation) {
          messageData.evaluation = msg.evaluation; // Complete evaluation: { verdict, feedback, followUpQuestion }
        }
        
        // Store raw evaluation data if available (before formatting)
        if (msg.rawEvaluation) {
          messageData.rawEvaluation = msg.rawEvaluation;
        }
        
        // Store feedback if separate from content
        if (msg.feedback && msg.feedback !== msg.content) {
          messageData.feedback = msg.feedback; // Raw feedback (not formatted)
        }
        
        // Store follow-up question if present in message
        if (msg.followUpQuestion) {
          messageData.followUpQuestion = msg.followUpQuestion;
        }
        
        return messageData;
      });
  };

  const saveProgress = (questionIndex, completed, messagesToSave = null) => {
    if (problemId) {
      // Progress is now derived from conversations in hld-attempt-${problemId}
      // No need for a separate progress key - progress can be calculated from deepDiveConversations
      onProgress(questionIndex, deepDiveQuestions.length);
      
      // Save conversation history for current question
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      
      // Store conversations per question
      if (!attemptData.deepDiveConversations) {
        attemptData.deepDiveConversations = [];
      }
      
      // Use provided messages if available, otherwise use state (may be stale due to async updates)
      const messagesForSaving = messagesToSave || chatMessages;
      
      // IMPORTANT: Filter messages to only include those belonging to the current question
      // This ensures each question only stores its own conversation, not messages from previous questions
      const messagesForCurrentQuestion = filterMessagesForQuestion(messagesForSaving, currentQuestionIndex);
      
      // Filter conversation to only include questions, user answers, and AI feedback
      // Use the filtered messages for current question to ensure we only save this question's data
      const filteredConversation = filterConversationForStorage(messagesForCurrentQuestion);
      
      // Extract the main question text from conversation (not from problem data)
      const mainQuestion = filteredConversation.find(msg => 
        msg.isQuestion && !msg.isFollowUp
      )?.content || '';
      
      // Update or add conversation for current question
      if (currentQuestionIndex < deepDiveQuestions.length) {
        const existingIndex = attemptData.deepDiveConversations.findIndex(c => c.questionIndex === currentQuestionIndex);
        
        // Only save: question text, question index, and filtered conversation (questions, answers, feedback)
        // Do NOT save: questionId, expectedAnswers, or any other problem data
        // IMPORTANT: Save ALL 3 fields from evaluation: verdict, feedback, followUpQuestion
        const conversationData = {
          questionIndex: currentQuestionIndex,
          question: mainQuestion, // Only the question text from conversation
          conversation: filteredConversation // All messages: questions, user answers, feedback (with evaluation), follow-up questions
        };
        
        if (existingIndex >= 0) {
          attemptData.deepDiveConversations[existingIndex] = conversationData;
        } else {
          attemptData.deepDiveConversations.push(conversationData);
        }
        
        attemptData.timestamp = Date.now();
        attemptData.lastDeepDiveQuestionIndex = currentQuestionIndex; // Save current question index
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));

        // Fire-and-forget backend save — localStorage is the source of truth during the session
        postRequest(
          process.env.REACT_APP_URL + '/hld-attempt/update-metadata',
          {
            userId: userId,
            hldProblemId: problemId,
            deepDiveConversations: attemptData.deepDiveConversations
          }
        ).catch(() => {/* silent — localStorage already saved */});
      }
    }
  };

  const handleSubmitAnswer = async () => {
    if (!userAnswer.trim() || !currentQuestion) return;

    setLoading(true);
    setFeedback(null);

    // Add user answer to chat
    const userMessage = {
      id: `user-answer-${Date.now()}`,
      messageType: MESSAGE_TYPES.USER_ANSWER,
      type: 'user', // Keep for backward compatibility
      content: userAnswer.trim(),
      timestamp: new Date().toISOString()
    };
    
    // Get updated messages for saving (will be used later when saving feedback)
    let updatedMessagesWithAnswer = [];
    setChatMessages(prev => {
      updatedMessagesWithAnswer = [...prev, userMessage];
      return updatedMessagesWithAnswer;
    });

    try {
      // Get AI evaluation
      const evaluationPrompt = HLD_EVALUATION_PROMPT(
        currentQuestion.question,
        currentQuestion.expectedAnswers || [],
        userAnswer.trim(),
        problemTitle
      );

      const evaluationResponse = await getAIFeedbackSync(
        evaluationPrompt,
        HLD_EVALUATION_SCHEMA,
        'HLD_EVALUATION'
      );

      // Parse the evaluation response - handle both string and object formats
      // getAIFeedbackSync should return response.data.response which is a JSON string
      // But handle case where full API response object might be returned
      let rawResponse = evaluationResponse;
      
      // If we got the full API response object, extract the response string
      if (rawResponse && typeof rawResponse === 'object' && rawResponse.success && rawResponse.data) {
        // Check if data.response exists (JSON string)
        if (rawResponse.data.response) {
          rawResponse = rawResponse.data.response;
        } else if (rawResponse.data && typeof rawResponse.data === 'object') {
          // Check if data itself is the evaluation object
          rawResponse = rawResponse.data;
        }
      }
      
      let evaluation = rawResponse;
      
      // If response is a string (JSON string), parse it
      if (typeof rawResponse === 'string') {
        try {
          // Try parsing the JSON string
          evaluation = JSON.parse(rawResponse);
        } catch (e) {
          // Fallback: create default evaluation object
          evaluation = { verdict: 'PARTIAL', feedback: rawResponse, followUpQuestion: '' };
        }
      } else if (typeof rawResponse === 'object' && rawResponse !== null) {
        // Already parsed object (direct evaluation object)
        evaluation = rawResponse;
      } else {
        evaluation = { verdict: 'PARTIAL', feedback: 'Unable to parse evaluation response', followUpQuestion: '' };
      }

      setFeedback(evaluation);

      // Extract all 3 fields from evaluation: verdict, feedback, followUpQuestion
      const verdict = evaluation.verdict || 'PARTIAL';
      const feedbackText = evaluation.feedback || evaluation.correctness || '';
      // IMPORTANT: Extract followUpQuestion - check multiple possible field names and handle empty strings
      let followUpQ = '';
      
      // Check all possible field names for follow-up question
      if (evaluation.followUpQuestion) {
        followUpQ = String(evaluation.followUpQuestion).trim();
      } else if (evaluation.follow_up_question) {
        followUpQ = String(evaluation.follow_up_question).trim();
      } else if (evaluation.followUp) {
        followUpQ = String(evaluation.followUp).trim();
      } else if (evaluation.followup_question) {
        followUpQ = String(evaluation.followup_question).trim();
      } else if (evaluation.followupQuestion) {
        followUpQ = String(evaluation.followupQuestion).trim();
      }
      
      // Additional check: if followUpQ is still empty, check if it's in a nested structure
      if (!followUpQ && evaluation.data && evaluation.data.followUpQuestion) {
        followUpQ = String(evaluation.data.followUpQuestion).trim();
      }
      
      // Format feedback message clearly showing what's correct and what's wrong
      let feedbackContent = feedbackText;
      
      // Add verdict context at the start
      const verdictText = verdict === 'PASS' 
        ? '✅ **Assessment: Your answer covers the key points well.**' 
        : verdict === 'PARTIAL'
        ? '⚠️ **Assessment: Your answer is partially correct, but some important aspects need improvement.**'
        : '❌ **Assessment: Your answer needs improvement. Several key points are missing or incorrect.**';
      
      feedbackContent = `${verdictText}\n\n${feedbackContent}`;

      // Add feedback message to chat with complete AI evaluation data (all 3 fields stored together)
      const feedbackMessage = {
        id: `feedback-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_FEEDBACK,
        type: 'ai', // Keep for backward compatibility
        content: feedbackContent, // Formatted feedback with verdict context
        timestamp: new Date().toISOString(),
        verdict: verdict, // PASS/PARTIAL/FAIL
        isFeedback: true,
        // Store complete AI evaluation object with all 3 fields
        evaluation: {
          verdict: verdict,
          feedback: feedbackText, // Raw feedback (not formatted)
          followUpQuestion: followUpQ // Follow-up question
        },
        // Store raw feedback before formatting (if different from content)
        feedback: feedbackText,
        // Store follow-up question directly in message
        followUpQuestion: followUpQ
      };
      // Add feedback message and follow-up question together to ensure both are saved
      const messagesToAdd = [feedbackMessage];
      
      // Add follow-up question if available (support infinite follow-ups)
      // IMPORTANT: Check if followUpQ exists and is not empty after trimming
      const trimmedFollowUpQ = followUpQ ? String(followUpQ).trim() : '';
      if (trimmedFollowUpQ && trimmedFollowUpQ.length > 0) {
        // Add to follow-up questions array
        setFollowUpQuestions(prev => [...prev, trimmedFollowUpQ]);
      
        // Add follow-up question to conversation
        const followUpMessage = {
          id: `followup-question-${Date.now()}`,
          messageType: MESSAGE_TYPES.FOLLOWUP_QUESTION,
          type: 'ai', // Keep for backward compatibility
          content: trimmedFollowUpQ,
          timestamp: new Date().toISOString(),
          isFollowUp: true,
          isQuestion: true
        };
        messagesToAdd.push(followUpMessage);
      }
      // No automatic progression - user can manually move to next question when ready
      
      setChatMessages(prev => {
        // Check if user answer is in prev (it may have been cleared by a polling reload during AI processing)
        const hasUserAnswer = prev.some(m => m.id === userMessage.id);
        // Always ensure user answer is present — re-add it if the polling reset wiped it out
        const base = hasUserAnswer ? prev : [...prev, userMessage];
        const updated = [...base, ...messagesToAdd];

        // Save immediately with the updated messages (don't rely on state which may be stale)
        setTimeout(async () => {
          // Pass the updated messages directly to saveProgress to avoid state staleness
          await saveProgress(currentQuestionIndex, completedQuestions, updated);
        }, 100);
        return updated;
      });
    } catch (error) {
      const errorMessage = {
        id: `error-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_FEEDBACK, // Error messages are AI feedback
        type: 'ai', // Keep for backward compatibility
        content: '❌ An error occurred while evaluating your answer. Please try again.',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setUserAnswer('');
      // Clear saved userAnswer from localStorage after submitting
      if (problemId && currentQuestionIndex !== undefined) {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedUserAnswerKey = `deepDiveUserAnswer_${currentQuestionIndex}`;
        delete attemptData[savedUserAnswerKey];
        attemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      }
    }
  };

  const handleHint = async () => {
    if (loading) return;

    setLoading(true);
    
    // Add hint request message to chat
    const hintRequestMessage = {
      id: `hint-user-${Date.now()}`,
      messageType: MESSAGE_TYPES.HINT_ASKED,
      type: 'user',
      content: 'can you give me a hint?',
      timestamp: new Date().toISOString(),
      isHintRequest: true // Mark as hint request
    };
    setChatMessages(prev => [...prev, hintRequestMessage]);

    try {
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      
      // Get current question context
      const currentQuestionText = currentQuestion?.question || deepDiveQuestions[currentQuestionIndex]?.question || '';
      
      // Build conversation history for context
      const conversationHistory = chatMessages
        .filter(msg => msg.type === 'user' && msg.content)
        .map(msg => msg.content)
        .join('\n');
      
      const hintPrompt = DEEP_DIVE_HINT_PROMPT(problemTitle || 'System Design', currentQuestionText, conversationHistory || '');

      const aiHintResponse = await getAIFeedbackSync(
        hintPrompt,
        `Give me a hint for this question: ${currentQuestionText}`,
        userId,
        'anthropic',
        null,
        null,
        {
          maxRetries: 3
        }
      );

      // Parse hint response
      let hintText = '';
      if (typeof aiHintResponse === 'string') {
        try {
          const parsed = JSON.parse(aiHintResponse);
          hintText = parsed.hint || parsed.response || aiHintResponse;
        } catch (e) {
          hintText = aiHintResponse;
        }
      } else if (aiHintResponse && aiHintResponse.hint) {
        hintText = aiHintResponse.hint;
      } else if (aiHintResponse && aiHintResponse.response) {
        hintText = aiHintResponse.response;
      } else {
        hintText = JSON.stringify(aiHintResponse);
      }

      // Add hint response to chat (styled in yellow)
      const hintResponseMessage = {
        id: `hint-ai-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_HINT,
        type: 'ai',
        content: hintText,
        timestamp: new Date().toISOString(),
        isHint: true // Mark as hint for yellow styling
      };
      
      setChatMessages(prev => {
        const updated = [...prev, hintResponseMessage];
        // Save hint to progress
        setTimeout(async () => {
          await saveProgress(currentQuestionIndex, completedQuestions, updated);
        }, 100);
        return updated;
      });
    } catch (error) {
      const errorMessage = {
        id: `hint-error-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_FEEDBACK,
        type: 'ai',
        content: '⚠️ Sorry, I encountered an error while generating a hint. Please try again.',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFollowUp = async () => {
    if (!followUpAnswer.trim()) return;

    setLoading(true);

    // Add follow-up answer to chat
    const followUpAnswerMsg = {
      id: `user-followup-${Date.now()}`,
        messageType: MESSAGE_TYPES.USER_ANSWER, // Follow-up answer is still a user answer
        type: 'user', // Keep for backward compatibility
      content: followUpAnswer.trim(),
      timestamp: new Date().toISOString()
    };
    
    // Get updated messages for saving
    let updatedMessagesWithAnswer = [];
    setChatMessages(prev => {
      updatedMessagesWithAnswer = [...prev, followUpAnswerMsg];
      return updatedMessagesWithAnswer;
    });

    try {
      // Evaluate follow-up answer - no specific expected answers, just check if it's a good response
      const evaluationPrompt = HLD_EVALUATION_PROMPT(
        currentFollowUpQuestion,
        [], // No specific expected answers for follow-up - just evaluate if answer makes sense
        followUpAnswer.trim(),
        problemTitle
      );

      const evaluationResponse = await getAIFeedbackSync(
        evaluationPrompt,
        HLD_EVALUATION_SCHEMA,
        'HLD_EVALUATION'
      );

      // Parse the evaluation response - handle both string and object formats
      // getAIFeedbackSync should return response.data.response which is a JSON string
      // But handle case where full API response object might be returned
      let rawResponse = evaluationResponse;
      
      // If we got the full API response object, extract the response string
      if (rawResponse && typeof rawResponse === 'object' && rawResponse.success && rawResponse.data && rawResponse.data.response) {
        rawResponse = rawResponse.data.response;
      }
      
      let evaluation = rawResponse;
      
      // If response is a string (JSON string), parse it
      if (typeof rawResponse === 'string') {
        try {
          // Try parsing the JSON string
          evaluation = JSON.parse(rawResponse);
        } catch (e) {
          // Fallback: create default evaluation object
          evaluation = { verdict: 'PARTIAL', feedback: rawResponse, followUpQuestion: '' };
        }
      } else if (typeof rawResponse === 'object' && rawResponse !== null) {
        // Already parsed object (direct evaluation object)
        evaluation = rawResponse;
      } else {
        evaluation = { verdict: 'PARTIAL', feedback: 'Unable to parse evaluation response', followUpQuestion: '' };
      }

      // Extract all fields from evaluation: verdict, feedback, and followUpQuestion (support infinite follow-ups)
      const followUpVerdict = evaluation.verdict || 'PARTIAL';
      const followUpFeedbackText = evaluation.feedback || evaluation.correctness || '';
      // IMPORTANT: Extract followUpQuestion - check multiple possible field names and handle empty strings
      let nextFollowUpQ = '';
      if (evaluation.followUpQuestion) {
        nextFollowUpQ = String(evaluation.followUpQuestion).trim();
      } else if (evaluation.follow_up_question) {
        nextFollowUpQ = String(evaluation.follow_up_question).trim();
      } else if (evaluation.followUp) {
        nextFollowUpQ = String(evaluation.followUp).trim();
      }
      
      // Set feedback state
      setFeedback({
        verdict: followUpVerdict,
        feedback: followUpFeedbackText,
        followUpQuestion: nextFollowUpQ // Store next follow-up question if available
      });
      
      // Format follow-up feedback message clearly showing what's correct and what's wrong
      let followUpFeedbackContent = followUpFeedbackText;
      
      // Add verdict context at the start
      const followUpVerdictText = followUpVerdict === 'PASS' 
        ? '✅ **Assessment: Good! You\'ve addressed the follow-up question well.**' 
        : followUpVerdict === 'PARTIAL'
        ? '⚠️ **Assessment: You\'re on the right track, but this aspect could use more detail.**'
        : '❌ **Assessment: This aspect needs more clarification.**';
      
      followUpFeedbackContent = `${followUpVerdictText}\n\n${followUpFeedbackContent}`;

      // Add feedback to chat - may include another follow-up question (infinite follow-ups)
      const feedbackMessage = {
        id: `followup-feedback-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_FEEDBACK, // Follow-up feedback is still AI feedback
        type: 'ai', // Keep for backward compatibility
        content: followUpFeedbackContent, // Formatted feedback with verdict context
        timestamp: new Date().toISOString(),
        verdict: followUpVerdict, // PASS/PARTIAL/FAIL
        isFeedback: true,
        // Store complete AI evaluation object (may include next follow-up question)
        evaluation: {
          verdict: followUpVerdict,
          feedback: followUpFeedbackText, // Raw feedback (not formatted)
          followUpQuestion: nextFollowUpQ // Next follow-up question if available
        },
        // Store raw feedback before formatting (if different from content)
        feedback: followUpFeedbackText,
        // Store follow-up question if available
        followUpQuestion: nextFollowUpQ
      };
      
      // Add next follow-up question if available (infinite follow-ups)
      const messagesToAdd = [feedbackMessage];
      // IMPORTANT: Check if nextFollowUpQ exists and is not empty after trimming
      const trimmedNextFollowUpQ = nextFollowUpQ ? String(nextFollowUpQ).trim() : '';
      if (trimmedNextFollowUpQ && trimmedNextFollowUpQ.length > 0) {
        // Add to follow-up questions array
        setFollowUpQuestions(prev => [...prev, trimmedNextFollowUpQ]);
        
        // Add follow-up question to conversation
        const nextFollowUpMessage = {
          id: `followup-question-${Date.now()}`,
          messageType: MESSAGE_TYPES.FOLLOWUP_QUESTION,
          type: 'ai',
          content: trimmedNextFollowUpQ,
          timestamp: new Date().toISOString(),
          isFollowUp: true,
          isQuestion: true
        };
        messagesToAdd.push(nextFollowUpMessage);
      }
      
      // Get current messages including the follow-up answer we just added
      // Check if follow-up answer is already in state
      let messagesWithFollowUpAnswer = [];
      setChatMessages(prev => {
        // Check if follow-up answer is already in state
        const hasFollowUpAnswer = prev.some(m => m.id === followUpAnswerMsg.id);
        messagesWithFollowUpAnswer = hasFollowUpAnswer ? prev : [...prev, followUpAnswerMsg];
        return messagesWithFollowUpAnswer;
      });
      
      // Add feedback (and next follow-up question if available) to messages and save
      setChatMessages(prev => {
        // Use the messages that include follow-up answer
        const baseMessages = prev.some(m => m.id === followUpAnswerMsg.id) ? prev : messagesWithFollowUpAnswer;
        const updated = [...baseMessages, ...messagesToAdd];
        // Save immediately with the updated messages (don't rely on state which may be stale)
        setTimeout(async () => {
          await saveProgress(currentQuestionIndex, completedQuestions, updated);
        }, 100);
        return updated;
      });
      
      // No automatic progression - user can manually move to next question when ready
    } catch (error) {
      const errorMessage = {
        id: `error-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_FEEDBACK, // Error messages are AI feedback
        type: 'ai', // Keep for backward compatibility
        content: '❌ An error occurred while evaluating your answer. Please try again.',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setFollowUpAnswer('');
      // Clear saved followUpAnswer from localStorage after submitting
      if (problemId && currentQuestionIndex !== undefined) {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedFollowUpAnswerKey = `deepDiveFollowUpAnswer_${currentQuestionIndex}`;
        delete attemptData[savedFollowUpAnswerKey];
        attemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      }
    }
  };

  const handleRestartDiscussion = () => {
    if (!window.confirm('Restart this discussion? Your conversation for this question will be cleared.')) return;

    // Clear from localStorage
    if (problemId) {
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');

      if (attemptData.deepDiveConversations) {
        attemptData.deepDiveConversations = attemptData.deepDiveConversations.filter(
          c => c.questionIndex !== currentQuestionIndex
        );
      }
      delete attemptData[`deepDiveUserAnswer_${currentQuestionIndex}`];
      delete attemptData[`deepDiveFollowUpAnswer_${currentQuestionIndex}`];
      attemptData.timestamp = Date.now();
      localStorage.setItem(attemptKey, JSON.stringify(attemptData));

      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      postRequest(
        process.env.REACT_APP_URL + '/hld-attempt/update-metadata',
        { userId, hldProblemId: problemId, deepDiveConversations: attemptData.deepDiveConversations }
      ).catch(() => {});
    }

    // Reset state — show only the original question
    const questionMessage = {
      id: `question-${currentQuestionIndex}-${Date.now()}`,
      messageType: MESSAGE_TYPES.QUESTION,
      type: 'ai',
      content: currentQuestion.question,
      timestamp: new Date().toISOString(),
      isQuestion: true
    };
    setChatMessages([questionMessage]);
    setFeedback(null);
    setFollowUpQuestions([]);
    setUserAnswer('');
    setFollowUpAnswer('');
    hasUserTypedRef.current = false;
  };

  const moveToNextQuestion = () => {
    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex < deepDiveQuestions.length) {
      // Navigate to next question (will trigger useEffect to load conversation)
      navigateToQuestion(nextIndex);
    } else {
      // All questions completed
      const newCompleted = [...completedQuestions, currentQuestionIndex];
      setCompletedQuestions(newCompleted);
      saveProgress(deepDiveQuestions.length, newCompleted);
      const completionMessage = {
        id: `completion-${Date.now()}`,
        messageType: MESSAGE_TYPES.AI_FEEDBACK, // Completion message is AI feedback
        type: 'ai', // Keep for backward compatibility
        content: '🎉 Congratulations! You have completed all deep dive questions.',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, completionMessage]);
      setCurrentQuestionIndex(deepDiveQuestions.length); // Set to out of bounds to hide input
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isFollowUpPhase) {
        handleSubmitFollowUp();
      } else {
        handleSubmitAnswer();
      }
    }
  };

  // Navigate to a specific question
  const navigateToQuestion = (questionIndex) => {
    if (questionIndex >= 0 && questionIndex < deepDiveQuestions.length) {
      // Save current conversation before navigating (fire-and-forget)
      saveProgress(currentQuestionIndex, completedQuestions);
      
      // Save the question index to localStorage so we can restore it on refresh
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      attemptData.lastDeepDiveQuestionIndex = questionIndex;
      localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      
      // Reset state for new question
      // Don't clear userAnswer/followUpAnswer here - let the useEffect restore them from localStorage
      // The useEffect will handle clearing if no saved values exist
      setCurrentQuestionIndex(questionIndex);
      setFollowUpQuestions([]);
      setFeedback(null);
      
      // Close navigation drawer
      setQuestionNavOpen(false);
    }
  };

  // Get question status for navigation UI
  const getQuestionStatus = (index) => {
    const attemptKey = `hld-attempt-${problemId}`;
    const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    const conversations = attemptData.deepDiveConversations || [];
    const conversation = conversations.find(c => c.questionIndex === index);
    
    if (!conversation || !conversation.conversation || conversation.conversation.length === 0) {
      return 'not_started';
    }
    
    const messages = conversation.conversation;
    const hasAnswer = messages.some(m => m.type === 'user');
    const hasFeedback = messages.some(m => m.isFeedback);
    
    if (hasAnswer && hasFeedback) {
      return 'answered';
    } else if (hasAnswer) {
      return 'in_progress';
    }
    
    return 'not_started';
  };

  if (!currentQuestion) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="h6">Congratulations!</Typography>
          <Typography>You have completed all deep dive questions.</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box 
        className="feature-list-header"
        sx={{ 
          backgroundColor: '#f8f9fa',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.13
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => setQuestionNavOpen(true)}
            size="small"
            sx={{
              color: '#008080',
              '&:hover': {
                backgroundColor: '#e3f2fd'
              }
            }}
          >
            <Menu />
          </IconButton>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            color: '#333',
            fontSize: '1.1rem',
            lineHeight: 1.2
          }}>
            Deep Dives
          </Typography>
          <Chip 
            label={`Question ${currentQuestionIndex + 1} of ${deepDiveQuestions.length}`}
            size="small"
            sx={{ backgroundColor: '#008080', color: 'white' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentQuestionIndex > 0 && (
            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  await navigateToQuestion(currentQuestionIndex - 1);
                } catch (err) {
                }
              }}
              startIcon={<ChevronLeft />}
              size="small"
              sx={{
                borderColor: '#008080',
                color: '#008080',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                  borderColor: '#006666'
                }
              }}
            >
              Previous Question
            </Button>
          )}
          {currentQuestionIndex < deepDiveQuestions.length - 1 && (
            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  await navigateToQuestion(currentQuestionIndex + 1);
                } catch (err) {
                }
              }}
              endIcon={<ChevronRight />}
              size="small"
              sx={{
                borderColor: '#008080',
                color: '#008080',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                  borderColor: '#006666'
                }
              }}
            >
              Next Question
            </Button>
          )}
          <Tooltip title="Restart this question's discussion">
            <Button
              variant="outlined"
              onClick={handleRestartDiscussion}
              startIcon={<RestartAlt />}
              size="small"
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#fdecea',
                  borderColor: '#b71c1c'
                }
              }}
            >
              Restart
            </Button>
          </Tooltip>
        <Button
          variant="outlined"
          onClick={() => setDocumentVisible(!documentVisible)}
          startIcon={<Description />}
          size="small"
          sx={{
            borderColor: '#008080',
            color: '#008080',
            '&:hover': {
              backgroundColor: '#e3f2fd',
              borderColor: '#006666'
            }
          }}
        >
          {documentVisible ? 'Hide' : 'View Doc'}
        </Button>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ p: 1.5, flexShrink: 0, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {Math.round(progressPercent)}% Complete
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 8,
            backgroundColor: '#e0e0e0',
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: `${progressPercent}%`,
              height: '100%',
              backgroundColor: '#008080',
              transition: 'width 0.3s ease'
            }}
          />
        </Box>
      </Box>

      {/* Chat Area - Separate for each question */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        width: '100%',
        p: 2,
        pb: 2,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fafafa',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: '#a8a8a8'
          }
        }
      }}>
        {/* Question-specific chat header */}
        <Box sx={{ 
          mb: 2, 
          pb: 1, 
          borderBottom: '2px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="caption" sx={{ 
            color: '#666',
            fontWeight: 'medium',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Question {currentQuestionIndex + 1} Chat
          </Typography>
          <Chip 
            label={`${chatMessages.length} messages`}
            size="small"
            sx={{ 
              height: 20,
              fontSize: '0.65rem',
              backgroundColor: '#e3f2fd',
              color: '#1976d2'
            }}
          />
        </Box>
        <List sx={{ p: 0, width: '100%' }}>
          {chatMessages.map((message) => (
            <ListItem 
              key={message.id}
              sx={{ 
                p: 0, 
                mb: 2,
                flexDirection: 'column',
                alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                width: '100%'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                gap: 0.5,
                maxWidth: '60%',
                width: 'fit-content',
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
              }}>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: message.type === 'user' ? '#008080' : 
                           message.isQuestion ? '#e3f2fd' :
                           message.isFeedback ? '#fff9e6' :
                           message.isHint ? '#fff3cd' :
                           '#f8f9fa',
                  color: message.type === 'user' ? 'white' : 
                         message.isQuestion ? '#1976d2' : 
                         message.isFeedback ? '#8b5a00' :
                         message.isHint ? '#856404' :
                         'text.primary',
                  borderRadius: 2,
                  maxWidth: '100%',
                  width: 'fit-content',
                  minWidth: '80px',
                  border: message.type === 'user' ? 'none' :
                         message.isQuestion ? '1px solid #1976d2' : 
                         message.isFeedback ? '1px solid #ffd700' :
                         message.isHint ? '1px solid #ffeaa7' :
                         '1px solid #e0e0e0',
                  boxShadow: message.isQuestion ? '0 2px 8px rgba(25,118,210,0.2)' : 
                             message.isFeedback ? '0 2px 8px rgba(255,215,0,0.2)' :
                             message.isHint ? '0 2px 8px rgba(255,193,7,0.2)' :
                             '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <Typography variant="body2" sx={{ 
                    wordBreak: 'break-word', 
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px',
                    lineHeight: 1.5
                  }}>
                    {message.content}
                  </Typography>
                </Paper>
              </Box>
            </ListItem>
          ))}
          
          {loading && (
            <ListItem sx={{ p: 0, mb: 2, justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: '#008080' }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                  Processing...
                </Typography>
              </Box>
            </ListItem>
          )}
          
          <div ref={chatEndRef} />
        </List>
      </Box>

      {/* Input Area - Show when we have a question and need user input */}
      {currentQuestion && currentQuestionIndex < deepDiveQuestions.length && (
        // Show input if: not in follow-up phase, OR in follow-up phase with a follow-up question to answer
        (!isFollowUpPhase || (isFollowUpPhase && currentFollowUpQuestion)) ? (
          <Box sx={{ 
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fafbfc',
            borderTop: '1px solid #f0f0f0',
            p: 1,
            flexShrink: 0,
            backdropFilter: 'blur(10px)',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            zIndex: 10,
            mt: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              gap: 0.75, 
              alignItems: 'flex-end',
              backgroundColor: 'white',
              borderRadius: 3,
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0'
            }}>
              <SpeechToTextInput
                fullWidth
                multiline
                minRows={1}
                maxRows={8}
                value={isFollowUpPhase ? followUpAnswer : userAnswer}
                onChange={(e) => {
                  hasUserTypedRef.current = true;
                  if (isFollowUpPhase) {
                    followUpAnswerRef.current = e.target.value;
                    setFollowUpAnswer(e.target.value);
                  } else {
                    userAnswerRef.current = e.target.value;
                    setUserAnswer(e.target.value);
                  }
                }}
                placeholder={isFollowUpPhase 
                  ? `Answer the follow-up: ${currentFollowUpQuestion.substring(0, 50)}${currentFollowUpQuestion.length > 50 ? '...' : ''}`
                  : "Type your answer here or click the microphone to speak..."
                }
                variant="standard"
                size="small"
                disabled={loading}
                onKeyPress={handleKeyPress}
                sx={{
                  '& .MuiInput-root': {
                    fontSize: '14px',
                    minHeight: 'auto',
                    '&:before': { border: 'none' },
                    '&:after': { border: 'none' },
                    '&:hover:before': { border: 'none' },
                    '&:focus-within:before': { border: 'none' }
                  },
                  '& .MuiInput-input': {
                    padding: '8px 12px',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1
                    }
                  }
                }}
              />
              <Tooltip title="Get a hint">
                <Button
                  variant="outlined"
                  onClick={handleHint}
                  disabled={loading}
                  sx={{
                    minWidth: 'auto',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    borderColor: '#f59e0b',
                    color: '#f59e0b',
                    flexShrink: 0,
                    '&:hover': { 
                      borderColor: '#d97706',
                      backgroundColor: '#fef3c7',
                      transform: 'scale(1.05)'
                    },
                    '&:disabled': {
                      borderColor: '#e5e7eb',
                      color: '#d1d5db'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Lightbulb sx={{ fontSize: 14, margin: 'auto' }} />
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                onClick={isFollowUpPhase ? handleSubmitFollowUp : handleSubmitAnswer}
                disabled={loading || (isFollowUpPhase ? !followUpAnswer.trim() : !userAnswer.trim())}
                sx={{
                  background: 'linear-gradient(135deg, #008080 0%, #006666 100%)',
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  boxShadow: '0 2px 8px rgba(0,128,128,0.3)',
                  flexShrink: 0,
                  '&:hover': { 
                    background: 'linear-gradient(135deg, #006666 0%, #004d4d 100%)',
                    boxShadow: '0 4px 12px rgba(0,128,128,0.4)',
                    transform: 'scale(1.05)'
                  },
                  '&:disabled': {
                    background: '#e5e7eb',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Send sx={{ color: 'white', fontSize: 14 }} />}
              </Button>
            </Box>
          </Box>
        ) : null
      )}

      {/* Question Navigation Drawer */}
      <Drawer
        anchor="left"
        open={questionNavOpen}
        onClose={() => setQuestionNavOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 320,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
              Questions
            </Typography>
            <IconButton
              onClick={() => setQuestionNavOpen(false)}
              size="small"
            >
              <ChevronRight />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List sx={{ p: 0 }}>
            {deepDiveQuestions.map((question, index) => {
              const status = getQuestionStatus(index);
              const isCurrent = index === currentQuestionIndex;
              const questionPreview = question.question.substring(0, 60) + (question.question.length > 60 ? '...' : '');
              
              return (
                <ListItem
                  key={index}
                  onClick={async () => {
                    try {
                      await navigateToQuestion(index);
                    } catch (err) {
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 0.5,
                    backgroundColor: isCurrent ? '#e3f2fd' : 'transparent',
                    '&:hover': {
                      backgroundColor: isCurrent ? '#e3f2fd' : '#f5f5f5'
                    },
                    border: isCurrent ? '2px solid #008080' : '1px solid transparent',
                    p: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                    <Box sx={{ mt: 0.5 }}>
                      {status === 'answered' ? (
                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                      ) : status === 'in_progress' ? (
                        <RadioButtonUnchecked sx={{ color: '#ff9800', fontSize: 20 }} />
                      ) : (
                        <RadioButtonUnchecked sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: isCurrent ? 'bold' : 'normal',
                          color: isCurrent ? '#008080' : '#333',
                          mb: 0.5
                        }}
                      >
                        Question {index + 1}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#666',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {questionPreview}
                      </Typography>
                      {status === 'answered' && (
                        <Chip 
                          label="Answered" 
                          size="small" 
                          sx={{ 
                            mt: 0.5,
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: '#4caf50',
                            color: 'white'
                          }} 
                        />
                      )}
                      {status === 'in_progress' && (
                        <Chip 
                          label="In Progress" 
                          size="small" 
                          sx={{ 
                            mt: 0.5,
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: '#ff9800',
                            color: 'white'
                          }} 
                        />
                      )}
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}